/**
 * Service de détection automatique des templates
 * Utilise les vrais endpoints du backend Django
 */

import axios from 'axios';
import { TemplateDiscoveryService } from './templateService';

// Configuration API
// Utiliser la variable d'environnement VITE_API_URL avec fallback pour localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api';
console.log('🔧 autoTemplateSync - API_BASE_URL utilisé:', API_BASE_URL);

// Créer un client axios avec intercepteur pour Idempotency-Key
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter Idempotency-Key aux requêtes POST (uniquement si pas en localhost)
apiClient.interceptors.request.use((config) => {
  if ((config.method === 'post' || config.method === 'POST') && 
      !API_BASE_URL.includes('localhost') && 
      !API_BASE_URL.includes('127.0.0.1')) {
    // Générer un Idempotency-Key unique pour chaque requête POST (uniquement en production/dev distant)
    const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    config.headers['Idempotency-Key'] = idempotencyKey;
  }
  return config;
});

export interface DetectedTemplate {
  template_id: string;
  component_name: string;
  file_path: string;
  name: string;
  category: string;
  industry: string;
  style: string;
  tags: string[];
  is_active: boolean;
  has_photo: boolean;
  pages_count: number;
  color_scheme: string;
  layout_type: string;
  preview_url: string;
  thumbnail_url: string;
}

export interface AutoDetectResponse {
  status: string;
  message: string;
  synced_count: number;
  templates: DetectedTemplate[];
}

export interface ScanResponse {
  status: string;
  message: string;
  count: number;
  templates: DetectedTemplate[];
}

/**
 * Détecte automatiquement les templates via l'API backend
 */
export const detectTemplatesInFolder = async (): Promise<DetectedTemplate[]> => {
  try {
    console.log('🔍 Détection automatique des templates...');
    console.log('📍 detectTemplatesInFolder - API_BASE_URL utilisé:', API_BASE_URL);
    console.log('📍 detectTemplatesInFolder - URL complète:', `${API_BASE_URL}/templates/auto-detect/`);
    
    const response = await apiClient.get('/templates/auto-detect/');
    const data: AutoDetectResponse = response.data;
    
    if (data.status === 'success') {
      console.log(`✅ ${data.synced_count || 0} templates détectés et synchronisés`);
      return data.templates || [];
    } else {
      console.warn('⚠️ Erreur détection:', data.message);
      // Fallback sur les templates locaux
      const localTemplates = TemplateDiscoveryService.discoverTemplates() as unknown as DetectedTemplate[];
      console.log(`⚠️ Utilisation des templates locaux (fallback): ${localTemplates.length} templates`);
      return localTemplates;
    }
  } catch (error) {
    console.error('❌ Erreur API détection:', error);
    // Fallback sur les templates locaux en cas d'erreur
    const localTemplates = TemplateDiscoveryService.discoverTemplates() as unknown as DetectedTemplate[];
    console.log(`⚠️ Utilisation des templates locaux (fallback après erreur): ${localTemplates.length} templates`);
    return localTemplates;
  }
};

/**
 * Scanne les templates sans synchroniser (pour prévisualisation)
 */
export const scanTemplatesInFolder = async (): Promise<DetectedTemplate[]> => {
  try {
    console.log('🔍 Scan des templates...');
    console.log('📍 scanTemplatesInFolder - API_BASE_URL utilisé:', API_BASE_URL);
    console.log('📍 scanTemplatesInFolder - URL complète:', `${API_BASE_URL}/templates/scan/`);
    
    const response = await apiClient.get('/templates/scan/');
    const data: ScanResponse = response.data;
    
    if (data.status === 'success') {
      console.log(`📁 ${data.count || 0} templates trouvés dans le dossier`);
      return data.templates || [];
    } else {
      console.warn('⚠️ Erreur scan:', data.message);
      // Fallback sur les templates locaux
      const localTemplates = TemplateDiscoveryService.discoverTemplates() as unknown as DetectedTemplate[];
      console.log(`⚠️ Utilisation des templates locaux (fallback): ${localTemplates.length} templates`);
      return localTemplates;
    }
  } catch (error) {
    console.error('❌ Erreur API scan:', error);
    // Fallback sur les templates locaux en cas d'erreur
    const localTemplates = TemplateDiscoveryService.discoverTemplates() as unknown as DetectedTemplate[];
    console.log(`⚠️ Utilisation des templates locaux (fallback après erreur): ${localTemplates.length} templates`);
    return localTemplates;
  }
};

/**
 * Compare les templates détectés avec ceux déjà synchronisés
 */
export const getNewTemplates = (
  detectedTemplates: DetectedTemplate[],
  syncedTemplates: any[]
): DetectedTemplate[] => {
  const syncedIds = syncedTemplates.map(t => t.template_id);
  
  return detectedTemplates.filter(template => 
    !syncedIds.includes(template.template_id)
  );
};

/**
 * Convertit un template détecté en format API
 */
export const convertToApiFormat = (template: DetectedTemplate) => {
  return {
    template_id: template.template_id,
    component_name: template.component_name,
    file_path: template.file_path,
    name: template.name,
    category: template.category,
    industry: template.industry,
    style: template.style,
    tags: template.tags,
    is_active: template.is_active,
    has_photo: template.has_photo,
    pages_count: template.pages_count,
    color_scheme: template.color_scheme,
    layout_type: template.layout_type,
    preview_url: template.preview_url,
    thumbnail_url: template.thumbnail_url
  };
};
